/**
 * Main application component that handles routing and authentication structure.
 * Provides both Auth0-protected and admin-only routes with appropriate layouts.
 *
 * Key Routes:
 * - /admin/*: Admin panel routes (JWT auth)
 * - /login: Public authentication page
 * - /dashboard: Main user dashboard
 * - /accounts/*: Facebook page management
 * - /settings/*: Application configuration
 *
 * @see {@link components/Auth0Provider.tsx} for authentication setup
 * @see {@link components/Layout.tsx} for main UI structure
 */

import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Auth0ProviderWrapper from "./components/Auth0Provider";
import { initializeAxiosInterceptor } from "./lib/axios-interceptor";
import FaviconGenerator from "./components/FaviconGenerator";

// Components
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
// COMMENTED OUT FOR FACEBOOK REVIEW - Dashboard not needed for review
// import Dashboard from "./pages/Dashboard";
import FacebookPages from "./pages/FacebookPages";
import FacebookPageDetail from "./pages/FacebookPageDetail";
import PromptSettings from "./pages/PromptSettings";
import ModerationDashboard from "./pages/ModerationDashboard";
import ReviewQueue from "./pages/ReviewQueue";
// COMMENTED OUT FOR FACEBOOK REVIEW - CommentInsights not needed for review
// import CommentInsights from "./pages/CommentInsights";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import CallbackPage from "./pages/CallbackPage";
import FacebookCallbackPage from "./pages/FacebookCallbackPage";
import InstagramCallbackPage from "./pages/InstagramCallbackPage";
import DatabaseOverview from "./pages/DatabaseOverview";
// Session monitoring removed - no expiration
import { FacebookPageProvider } from "./contexts/FacebookPageContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { PostFilterProvider } from "./contexts/PostFilterContext";
import { ModerationToastProvider } from "./components/ModerationToast";

// Admin Components
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import DatabaseViewer from "./pages/DatabaseViewer";
import AdminUsers from "./pages/AdminUsers";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminPages from "./pages/AdminPages";
import AdminPageDetail from "./pages/AdminPageDetail";

// Public pages
import Landing from "./pages/LandingPage/Landing";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import About from "./pages/About";
// COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation feature
// import MessageAutoreplyPage from "pages/MessageAutoreplyPage";
import Pricing from "pages/Pricing";
import Blog from "pages/Blog";
import Contact from "pages/Contact";
import Feedback from "pages/Feedback";

// Inner component that has access to Auth0 hooks
function AppWithAuth() {
  const { getAccessTokenSilently, isLoading } = useAuth0();

  useEffect(() => {
    // Initialize axios interceptor once Auth0 is ready
    if (!isLoading) {
      initializeAxiosInterceptor(getAccessTokenSilently);
    }
  }, [getAccessTokenSilently, isLoading]);

  const impUserId =
    typeof window !== "undefined"
      ? sessionStorage.getItem("impersonateUserId")
      : null;
  const impUserName =
    typeof window !== "undefined"
      ? sessionStorage.getItem("impersonateUserName")
      : null;

  return (
    <Router>
      {impUserId && (
        <div
          style={{
            background: "#dc2626",
            color: "white",
            padding: "8px 16px",
            textAlign: "center",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Acting as {impUserName || "user"} —{" "}
          <button
            onClick={() => {
              sessionStorage.removeItem("impersonateUserId");
              sessionStorage.removeItem("impersonateUserName");
              window.location.href = "/admin/users";
            }}
            style={{
              textDecoration: "underline",
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Stop
          </button>
        </div>
      )}
      <Routes>
        {/* Public landing & legal */}
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/about" element={<About />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />

        {/* Admin routes (no Auth0) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
        <Route path="/admin/pages" element={<AdminPages />} />
        <Route path="/admin/pages/:pageId" element={<AdminPageDetail />} />
        <Route
          path="/admin/database/:collection"
          element={<DatabaseViewer />}
        />

        {/* Public auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/facebook-callback" element={<FacebookCallbackPage />} />
        <Route path="/instagram-callback" element={<InstagramCallbackPage />} />

        {/* Protected routes with sidebar layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SubscriptionProvider>
                <FacebookPageProvider>
                  <PostFilterProvider>
                    <ModerationToastProvider>
                      <Layout />
                    </ModerationToastProvider>
                  </PostFilterProvider>
                </FacebookPageProvider>
              </SubscriptionProvider>
            </ProtectedRoute>
          }
        >
          {/* Dashboard routes commented out - not needed for Facebook App Review */}
          {/* <Route path="dashboard" element={<Dashboard />} /> */}
          {/* <Route path="global-dashboard" element={<Dashboard />} /> */}
          <Route path="accounts" element={<FacebookPages />} />
          <Route
            path="accounts/:pageId"
            element={<FacebookPageDetail />}
          />
          {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation feature */}
          {/* <Route
            path="accounts/:pageId/autoreply"
            element={<MessageAutoreplyPage />}
          /> */}
          <Route
            path="accounts/:pageId/moderation"
            element={<ModerationDashboard />}
          />
          <Route
            path="accounts/:pageId/moderation-rules"
            element={<ModerationDashboard view="rules" />}
          />
          <Route
            path="accounts/:pageId/comments"
            element={<ModerationDashboard view="comments" />}
          />
          <Route
            path="accounts/:pageId/review"
            element={<ReviewQueue />}
          />
          {/* Page Insights commented out - not needed for Facebook App Review */}
          {/* <Route
            path="accounts/:pageId/insights"
            element={<CommentInsights />}
          /> */}
          {/* Backward-compat: old /facebook-pages links redirect to /accounts */}
          <Route path="facebook-pages/*" element={<Navigate to="/accounts" replace />} />
          <Route path="database-overview" element={<DatabaseOverview />} />
          <Route path="prompt-settings" element={<PromptSettings />} />
          <Route path="moderation-rules" element={<ModerationDashboard />} />
          <Route path="settings" element={<Settings />} />
          <Route path="help" element={<Help />} />
          <Route path="feedback" element={<Feedback />} />
          {/* Session testing removed */}
        </Route>

        {/* Fallback to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Main App component that wraps everything with Auth0
function App() {
  return (
    <Auth0ProviderWrapper>
      <FaviconGenerator />
      <AppWithAuth />
    </Auth0ProviderWrapper>
  );
}

export default App;
